import { useMemo, useState } from "react";

import { getRelationLabel, getTariffs, normalizeTariff, saveTariffs } from "../utils/tariffImportUtils";
import { SuccessToast, useSuccessFeedback } from "./useSuccessFeedback";

function toJson(value) {
  return JSON.stringify(value || [], null, 2);
}

function parseJson(value, fallback) {
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : fallback;
  } catch (error) {
    return fallback;
  }
}

const calculationTypes = [
  "Fixbetrag",
  "Prozent auf Grundfracht",
  "Tabelle nach Warenwert",
  "Tabelle nach Gewicht und Entfernung",
  "Manuell",
];

export default function SurchargeManagementPage() {
  const [tariffs, setTariffs] = useState(() => getTariffs().map(normalizeTariff));
  const [selectedId, setSelectedId] = useState(() => tariffs[0]?.id || "");
  const { successVisible, triggerSuccessFeedback } = useSuccessFeedback();
  const selectedTariff = useMemo(() => tariffs.find((tariff) => tariff.id === selectedId) || null, [tariffs, selectedId]);

  const updateSelectedTariff = (patch) => {
    setTariffs((current) => current.map((tariff) => tariff.id === selectedId ? normalizeTariff({ ...tariff, ...patch, updatedAt: new Date().toISOString() }) : tariff));
  };

  const save = () => {
    saveTariffs(tariffs);
    triggerSuccessFeedback();
  };

  return (
    <div className="page-stack tariff-import-page">
      <SuccessToast visible={successVisible} />
      <div>
        <p className="eyebrow">Admin</p>
        <h1 className="page-title">Zuschlagsverwaltung</h1>
        <p className="section-text">Versicherung, Maut, Diesel und sonstige Zuschlaege pro Tarif pflegen.</p>
      </div>

      <section className="card tariff-import-card">
        <div className="section-head">
          <div>
            <h2>Tarif auswaehlen</h2>
            <p className="section-text">Die Zuschlaege werden direkt am Tarifdatensatz gespeichert.</p>
          </div>
          <button type="button" className="button button-primary" onClick={save}>Aenderungen speichern</button>
        </div>

        {tariffs.length === 0 ? (
          <div className="empty-state">Noch keine Tarife importiert.</div>
        ) : (
          <label className="field">
            <span>Tarif</span>
            <select value={selectedId} onChange={(event) => setSelectedId(event.target.value)}>
              {tariffs.map((tariff) => (
                <option key={tariff.id} value={tariff.id}>{tariff.status} - {tariff.carrierName} / {getRelationLabel(tariff)} / {tariff.tariffType} / {tariff.tariffName}</option>
              ))}
            </select>
          </label>
        )}
      </section>

      {selectedTariff && (
        <section className="card tariff-import-card">
          <div className="section-head">
            <div>
              <h2>Zuschlagsdaten</h2>
              <p className="section-text">{selectedTariff.carrierName} / {getRelationLabel(selectedTariff)} / {selectedTariff.tariffType}</p>
            </div>
            <div className="entry-summary"><span>{selectedTariff.validFrom || "-"}</span><span>{selectedTariff.validTo || "-"}</span></div>
          </div>

          <div className="form-grid columns-3 tariff-form-grid">
            <label className="field"><span>Name</span><input value={selectedTariff.tariffName || ""} onChange={(event) => updateSelectedTariff({ tariffName: event.target.value })} /></label>
            <label className="field"><span>Land / Relation</span><input value={selectedTariff.relation || selectedTariff.country || ""} onChange={(event) => updateSelectedTariff({ relation: event.target.value })} /></label>
            <label className="field"><span>Spedition</span><input value={selectedTariff.carrierName || ""} onChange={(event) => updateSelectedTariff({ carrierName: event.target.value })} /></label>
            <label className="field"><span>Tarifart</span><input value={selectedTariff.tariffType || ""} onChange={(event) => updateSelectedTariff({ tariffType: event.target.value })} /></label>
            <label className="field"><span>Gueltig von</span><input type="date" value={selectedTariff.validFrom || ""} onChange={(event) => updateSelectedTariff({ validFrom: event.target.value })} /></label>
            <label className="field"><span>Gueltig bis</span><input type="date" value={selectedTariff.validTo || ""} onChange={(event) => updateSelectedTariff({ validTo: event.target.value })} /></label>
            <label className="field"><span>Berechnungsart</span><select defaultValue="Tabelle nach Warenwert">{calculationTypes.map((type) => <option key={type}>{type}</option>)}</select></label>
            <label className="checkbox-card tariff-checkbox"><input type="checkbox" checked={selectedTariff.status === "active" || selectedTariff.isActive} readOnly /><span>Aktiv</span></label>

            <label className="field field-wide"><span>Versicherungstabellen JSON</span><textarea rows="9" value={toJson(selectedTariff.insuranceTable)} onChange={(event) => updateSelectedTariff({ insuranceTable: parseJson(event.target.value, selectedTariff.insuranceTable) })} /></label>
            <label className="field field-wide"><span>Mauttabellen Deutschland JSON</span><textarea rows="9" value={toJson(selectedTariff.toll)} onChange={(event) => updateSelectedTariff({ toll: parseJson(event.target.value, selectedTariff.toll) })} /></label>
            <label className="field field-wide"><span>Internationale Mautzuschlaege JSON</span><textarea rows="7" value={toJson(selectedTariff.internationalToll)} onChange={(event) => updateSelectedTariff({ internationalToll: parseJson(event.target.value, selectedTariff.internationalToll) })} /></label>
            <label className="field field-wide"><span>Dieselzuschlaege JSON</span><textarea rows="7" value={toJson(selectedTariff.dieselTable)} onChange={(event) => updateSelectedTariff({ dieselTable: parseJson(event.target.value, selectedTariff.dieselTable) })} /></label>
            <label className="field field-wide"><span>Sonstige Zuschlaege JSON</span><textarea rows="9" value={toJson(selectedTariff.surcharges)} onChange={(event) => updateSelectedTariff({ surcharges: parseJson(event.target.value, selectedTariff.surcharges) })} /></label>
          </div>
        </section>
      )}
    </div>
  );
}
